import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Boxes, Wrench, FolderTree, Percent, AlertTriangle } from 'lucide-react';
import { StatCard, WidgetPanel } from './DashboardShared';
import { catalogProductsOf, catalogServicesOf, pCategoriesOf, pSubCategoriesOf, sCategoriesOf, sSubCategoriesOf, salesOf } from '../../../data/store';

export const CatalogModule = ({ org }) => {
  const metrics = useMemo(() => {
    const products = catalogProductsOf(org.id);
    const services = catalogServicesOf(org.id);
    const pCats = pCategoriesOf(org.id).length + pSubCategoriesOf(org.id).length;
    const sCats = sCategoriesOf(org.id).length + sSubCategoriesOf(org.id).length;
    
    const sales = salesOf(org.id);
    const totalGst = sales.reduce((s, x) => s + (x.totalTax || 0), 0);

    // Sales item mapping
    // Quantity and money together. Quantity alone ranks a shop by its cheapest
    // line — a hundred sachets outsell four gas cylinders and tell the owner
    // nothing about where the takings came from.
    const itemSales = {};
    sales.forEach(sale => {
      (sale.lines || []).forEach(line => {
        const name = line.name || 'Unnamed';
        const at = itemSales[name] || { name, qty: 0, value: 0 };
        at.qty += Number(line.qty) || 0;
        at.value += Number(line.lineTotal ?? (line.price || 0) * (line.qty || 0)) || 0;
        itemSales[name] = at;
      });
    });

    const ranked = Object.values(itemSales).sort((a, b) => b.qty - a.qty);
    const mostSold = ranked.length ? ranked[0].qty : 0;
    // Each row is drawn against the leader, so the bars show how far ahead the
    // top seller actually is rather than just listing five names in order.
    const topSelling = ranked.slice(0, 5).map((r) => ({
      ...r,
      share: mostSold > 0 ? r.qty / mostSold : 0,
    }));

    // Dead stock / No sales
    const allItems = [...products, ...services];
    const deadStock = allItems.filter(item => !itemSales[item.name]).slice(0, 5);

    // Catalog Health
    let missingPrices = 0;
    let missingGst = 0;
    let missingImages = 0;
    allItems.forEach(item => {
      if (!item.basePrice && (!item.variants || item.variants.length === 0)) missingPrices++;
      if (!item.gstId) missingGst++;
      if (!item.image && (!item.images || item.images.length === 0)) missingImages++;
    });

    return { products: products.length, services: services.length, categories: pCats + sCats, totalGst, topSelling, deadStock, missingPrices, missingGst, missingImages };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <Boxes size={16} className="text-[var(--theme-primary)]" />
        Master Catalog
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Boxes} label="Total Products" value={metrics.products} iconColor="#14b8a6" />
        <StatCard icon={Wrench} label="Total Services" value={metrics.services} iconColor="#0ea5e9" />
        <StatCard icon={FolderTree} label="Categories" value={metrics.categories} suffix="groups" iconColor="#f43f5e" />
        <StatCard icon={Percent} label="GST Collected" value={`₹${metrics.totalGst.toFixed(2)}`} iconColor="#8b5cf6" />
      </div>

      <div className="dash-enter grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Top Selling Items">
          {/* A ranked list needs to show the gaps, not just the order. The bar
              behind each row is that item against the best seller, so a runaway
              leader looks like one and five items selling evenly look like that
              too — which the old list of green "42 sold" pills could not say. */}
          <ul className="flex flex-col divide-y divide-slate-50">
            {metrics.topSelling.length === 0 && (
              <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No sales yet</li>
            )}
            {metrics.topSelling.map((item, idx) => (
              <li key={item.name} className="flex items-center gap-2.5 py-2 min-w-0">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-slate-50 text-[10px] font-bold text-slate-400 tabular-nums">
                  {idx + 1}
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-[12px] font-bold text-[#0d0d0d]" title={item.name}>
                    {item.name}
                  </span>
                  <span className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                    <span
                      className="block h-full rounded-full bg-emerald-500 transition-all duration-700 ease-out"
                      style={{ width: `${Math.max(item.share * 100, 3)}%` }}
                    />
                  </span>
                </span>

                <span className="flex shrink-0 flex-col items-end">
                  <span className="text-[12px] font-bold text-[#0d0d0d] tabular-nums">
                    ₹{Math.round(item.value).toLocaleString('en-IN')}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 tabular-nums">{item.qty} sold</span>
                </span>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Non-Moving Items">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.deadStock.length === 0 && <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">All items are selling</li>}
            {metrics.deadStock.map((item, idx) => (
              <li key={idx} className="py-2.5 flex items-center justify-between text-xs">
                <span className="font-bold text-slate-500 truncate pr-4">{item.name}</span>
                <span className="font-bold text-slate-400 uppercase tracking-widest text-[10px]">0 sales</span>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Catalog Health">
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-100">
              <div className="flex items-center gap-2 text-xs font-bold text-orange-800">
                <AlertTriangle size={14} /> Missing Price
              </div>
              <span className="text-sm font-bold text-orange-600">{metrics.missingPrices}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-rose-50 border border-rose-100">
              <div className="flex items-center gap-2 text-xs font-bold text-rose-800">
                <AlertTriangle size={14} /> Missing GST
              </div>
              <span className="text-sm font-bold text-rose-600">{metrics.missingGst}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2 text-xs font-bold text-blue-800">
                <AlertTriangle size={14} /> Missing Images
              </div>
              <span className="text-sm font-bold text-blue-600">{metrics.missingImages}</span>
            </div>
          </div>
        </WidgetPanel>
      </div>
    </div>
  );
};

CatalogModule.propTypes = {
  org: PropTypes.object.isRequired,
};
